import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileSignature, PenLine } from 'lucide-react';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-amber-50 text-amber-800 border-amber-200',
  viewed: 'bg-amber-50 text-amber-900 border-amber-200',
  signed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

/**
 * Lists contracts linked to a booking.
 * Vendor: open contract detail. Client: sign when available.
 */
const BookingLinkedContracts = ({
  contracts = [],
  variant = 'vendor', // vendor | client
  className = '',
  createHref = null,
}) => {
  const list = Array.isArray(contracts) ? contracts : [];
  const showCreate = variant === 'vendor' && createHref;

  if (list.length === 0 && !showCreate) {
    return null;
  }

  const formatDate = (value) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  return (
    <section
      className={`rounded-lg border border-border bg-surface p-5 sm:p-6 shadow-sm ${className}`}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-ink flex items-center gap-2">
            <FileSignature className="w-4 h-4" />
            Contracts
          </h2>
          <p className="text-sm text-ink-secondary mt-0.5">
            Agreements linked to this booking
          </p>
        </div>
        {showCreate ? (
          <Link
            to={createHref}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-xs font-medium text-white hover:bg-black"
          >
            <PenLine className="w-3.5 h-3.5" />
            Create contract
          </Link>
        ) : null}
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-ink-tertiary">
          No contracts linked yet. Create one for the client to sign.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {list.map((contract) => {
            const status = String(contract.status || 'draft').toLowerCase();
            const signedLabel = formatDate(contract.signed_at);
            const openHref = contract.signing_path || contract.signing_url || null;
            const canSign =
              variant === 'client' && contract.can_be_signed && Boolean(openHref);
            const canViewSigned =
              variant === 'client' && status === 'signed' && Boolean(openHref);

            return (
              <li
                key={contract.id}
                className="flex flex-col gap-2 rounded-xl border border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-ink truncate">
                      {contract.title || contract.contract_number || 'Contract'}
                    </p>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${
                        STATUS_STYLES[status] || STATUS_STYLES.draft
                      }`}
                    >
                      {status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-ink-secondary">
                    {contract.contract_number || '—'}
                    {signedLabel ? (
                      <>
                        <span className="text-ink-tertiary"> · </span>
                        Signed {signedLabel}
                      </>
                    ) : null}
                    {status === 'draft' && variant === 'client' ? (
                      <span className="ml-1.5 text-ink-tertiary font-medium">
                        · Ready when you open to sign
                      </span>
                    ) : null}
                    {canSign && status !== 'draft' ? (
                      <span className="ml-1.5 text-amber-700 font-medium">
                        · Signature needed
                      </span>
                    ) : null}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {variant === 'vendor' ? (
                    <Link
                      to={`/vendor/contracts/${contract.id}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-ink hover:bg-surface-muted"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View
                    </Link>
                  ) : canSign ? (
                    <Link
                      to={openHref}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-xs font-medium text-white hover:bg-black"
                    >
                      <PenLine className="w-3.5 h-3.5" />
                      Sign
                    </Link>
                  ) : canViewSigned ? (
                    <Link
                      to={openHref}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View
                    </Link>
                  ) : (
                    <span className="inline-flex items-center rounded-lg border border-border px-3 py-2 text-xs font-medium text-ink-tertiary">
                      Not available
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default BookingLinkedContracts;
